import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import { EMPLOYEE_SUBSCRIPTION_PAIR_PATH } from '../../components/EoeOutlet/EoeOutlet.js';
import { TypeKeys } from '../actions/index.js';
import { addToActionHistory, isPreviousActionInProgress, updateActionStatePhase } from '../common/index.js';
import { ActionPhase } from '../common/storeUtils.js';
import { reduceCrudAction, reduceSubscriptionUsage, reduceSubscriptions } from './reducerUtils.js';
import type { AuthenticatedUserDataState } from '../../common/types/states.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { ActionTypes } from '../actions/index.js';
import type { ActionsHistory } from '../common/store.js';

export const subscriptionUsageType = {
  LOCAL_USAGE: 'localUsage',
  ROAMING_USAGE: 'roamingUsage',
};

export function authenticatedUserReducer(
  state: (AuthenticatedUserDataState & ActionsHistory) | undefined,
  action: ActionTypes
): (AuthenticatedUserDataState & ActionsHistory) | null {
  if (typeof state === 'undefined') {
    return null;
  }
  switch (action.type) {
    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS:
      if (isPreviousActionInProgress(action.type, state, 'subscriptionType', action.subscriptionType)) {
        return state;
      } else {
        return {
          ...state,
          actions: addToActionHistory(state, {
            phase: ActionPhase.IN_PROGRESS,
            value: action,
          }),
        };
      }

    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FULLFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptions(action.response, action.subscriptionType, state.subscriptions),
      };
    }

    case TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_EMPLOYEE_SUBSCRIPTIONS,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.CHECK_VALID_EMPLOYEE_SUBSCRIPTION: {
      if (
        state.subscriptions &&
        action.subscriptionToValidate !== undefined &&
        action.subscriptionToValidate !== EMPLOYEE_SUBSCRIPTION_PAIR_PATH &&
        // @ts-ignore
        !state.subscriptions[action.subscriptionType].find(
          (subscription: EmployeeSubscription) => subscription.subscriptionId === action.subscriptionToValidate
        )
      ) {
        return {
          ...state,
          errors: convertToCommonErrors(`subscription ${action.subscriptionToValidate} could not be found`, 404),
        };
      }
      return state;
    }

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.LOCAL_USAGE,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_LOCAL_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE:
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        subscriptions: reduceSubscriptionUsage(
          subscriptionUsageType.ROAMING_USAGE,
          action.subscriptionId,
          action.response,
          state.subscriptions
        ),
      };
    }

    case TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.GET_SUBSCRIPTION_ROAMING_USAGE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.POLL_FOR_MYSELF_CHANGES: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }
    case TypeKeys.POLL_FOR_MYSELF_CHANGES_FAILED: {
      if (state === null) {
        return state;
      }
      const latestChangeRef = action.params?.retainLatestChangeRef ? state.latestChangeRef : undefined;
      return {
        ...state,
        latestChangeRef,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }
    case TypeKeys.STOP_POLL_FOR_MYSELF_CHANGES: {
      if (state === null) {
        return state;
      }
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
      };
    }
    case TypeKeys.POLL_FOR_MYSELF_CHANGES_FULFILLED: {
      return {
        ...state,
        latestChangeRef: action.latestChangeRef,
        actions: updateActionStatePhase(
          TypeKeys.POLL_FOR_MYSELF_CHANGES,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING: {
      if (action.validationErrors?.length) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED:
    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.INITIATE_EMPLOYEE_ONBOARDING,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
      };
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.INITIATE_EMPLOYEE_ONBOARDING,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK: {
      return {
        ...state,
        ...reduceCrudAction(action, state),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESEND_EMAIL_VERIFICATION_LINK,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
      };
    }

    case TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESEND_EMAIL_VERIFICATION_LINK,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
      };
    }

    case TypeKeys.CLEAR_STATE: {
      return {};
    }

    case TypeKeys.CLEAR_EMPLOYEE_SUBS: {
      return {
        ...state,
        subscriptions: undefined,
      };
    }

    case TypeKeys.RESET_ERRORS: {
      return {
        ...state,
        errors: undefined,
      };
    }

    default:
      return state;
  }
}
